import type { LocalPrivateSpecialistsConfig } from './config';

/**
 * Satisfaction smoothed toward a prior, so 100% from 1 review does not outrank 95% from 299.
 * score = (satisfaction * count + PRIOR * WEIGHT) / (count + WEIGHT)
 */
const PRIOR_SATISFACTION = 80;
const PRIOR_WEIGHT = 20;

export const smoothedSatisfaction = (satisfaction?: number | null, count?: number | null) => {
  if (satisfaction == null || !count) return null;
  return (satisfaction * count + PRIOR_SATISFACTION * PRIOR_WEIGHT) / (count + PRIOR_WEIGHT);
};

/**
 * Search-side pre-filter for "has an in-person turn": online-booking doctor (not phone/bulk) with a
 * first free turn in the index. The index can be stale (it still lists turns of doctors whose booking
 * just closed), so the final say is Saman's `booking_available` — see LocalPrivateSpecialists.
 */
export const hasIndexedInPersonTurn = (hit: any) => !hit?.is_bulk && !!hit?.presence_freeturn;

/** «<span>اولین نوبت: <b>کمتر از یک ساعت دیگر</b></span>» of the search's booking action → plain text. */
export const firstTurnLabel = (hit: any): string => {
  const action = (hit?.actions ?? []).find((item: any) => /اولین نوبت/.test(item?.top_title ?? ''));
  return (action?.top_title ?? '').replace(/<[^>]*>/g, '').trim();
};

const EARTH_RADIUS_KM = 6371;
const toRad = (deg: number) => (deg * Math.PI) / 180;
/** Coordinates farther than this from the city point are data errors (e.g. «اصفهان» offices at 340 km). */
const IMPLAUSIBLE_KM = 100;

export const haversineKm = (a: { lat: number; lon: number }, b: { lat: number; lon: number }) => {
  const dLat = toRad(b.lat - a.lat);
  const dLon = toRad(b.lon - a.lon);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLon / 2) ** 2;
  return 2 * EARTH_RADIUS_KM * Math.asin(Math.sqrt(h));
};

/**
 * Neighbourhood check (config.near): true when a center lies within `near.radiusKm`, or — for a center
 * whose coordinates are missing, the 1/1 placeholder or implausibly far — when its `city_name` is the
 * configured city. Any center type counts (office, clinic, hospital).
 */
export const isInNeighbourhood = (hit: any, config: LocalPrivateSpecialistsConfig): boolean => {
  const { near } = config;
  if (!near) return true;
  return (hit?.centers ?? []).some((center: any) => {
    const map = center?.map;
    const hasCoords = Number.isFinite(map?.lat) && Number.isFinite(map?.lon) && !(map.lat === 1 && map.lon === 1);
    const km = hasCoords ? haversineKm(near, { lat: map.lat, lon: map.lon }) : null;
    if (km != null && km <= IMPLAUSIBLE_KM) return km <= near.radiusKm;
    return center?.city_name === config.cityName;
  });
};

/** Quality floor (config.minSatisfaction / minRates); profiles without enough reviews are left out. */
export const meetsQualityFloor = (hit: any, config: LocalPrivateSpecialistsConfig): boolean => {
  if (config.minSatisfaction != null && !(Number(hit?.satisfaction) >= config.minSatisfaction)) return false;
  if (config.minRates != null && !(Number(hit?.rates_count) >= config.minRates)) return false;
  return true;
};

const firstTurnTime = (hit: any) => {
  const value = Number(hit?.presence_freeturn);
  return Number.isFinite(value) && value > 0 ? value : Number.POSITIVE_INFINITY;
};

/**
 * Keeps doctors of the configured specialty (minus excluded ones), drops the profile's own doctor,
 * duplicates across merged search pages, doctors outside the neighbourhood and below the quality
 * floor. Order: `first-turn` = earliest free turn, then smoothed satisfaction; otherwise smoothed
 * satisfaction (doctors without reviews last, in the search's own order).
 */
export const rankLocalSpecialists = (results: any[] | undefined, config: LocalPrivateSpecialistsConfig, ownSlug?: string) => {
  const ownUrl = ownSlug ? `/dr/${ownSlug}/` : null;
  const seen = new Set<string>();
  const doctors = (results ?? []).filter(hit => {
    if (hit?.type && hit.type !== 'doctor') return false;
    if (ownUrl && (hit?.url === ownUrl || hit?.slug === ownSlug)) return false;
    const key = hit?.url ?? hit?.id;
    if (key && seen.has(key)) return false;
    if (key) seen.add(key);
    const expertise: string = hit?.display_expertise ?? '';
    if (!config.includeExpertise.some(text => expertise.includes(text))) return false;
    if (config.excludeExpertise?.some(text => expertise.includes(text))) return false;
    return isInNeighbourhood(hit, config) && meetsQualityFloor(hit, config);
  });

  return doctors
    .map((hit, index) => ({ hit, index, score: smoothedSatisfaction(hit?.satisfaction, hit?.rates_count) }))
    .sort((a, b) => {
      if (config.order === 'first-turn') {
        const byTurn = firstTurnTime(a.hit) - firstTurnTime(b.hit);
        if (byTurn) return byTurn;
      }
      if (a.score == null && b.score == null) return a.index - b.index;
      if (a.score == null) return 1;
      if (b.score == null) return -1;
      return b.score - a.score || a.index - b.index;
    })
    .map(item => item.hit);
};

/** «دکتر  نادر   ترکاشوند» → «نادر ترکاشوند». */
const doctorNameKey = (hit: any) =>
  String(hit?.title ?? hit?.display_name ?? '')
    .replace(/^\s*دکتر\s+/, '')
    .replace(/\s+/g, ' ')
    .trim();

/**
 * One card per doctor: many doctors have a second profile (office + clinic, e.g. Nader Torkashvand in
 * Malayer). Applied after the bookability check, so the first bookable profile in display order
 * (earliest turn) is the one kept.
 */
export const uniqueByDoctorName = <T>(doctors: T[]): T[] => {
  const seen = new Set<string>();
  return doctors.filter(hit => {
    const key = doctorNameKey(hit);
    if (!key) return true;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};
